import { useNodeId, useStore } from '@xyflow/react';
import { useMotionValue } from 'framer-motion';
import { type PointerEvent, useCallback, useMemo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import useFlow from '@/hooks/use-flow';
import useFlowStore from '@/store/flow';

export const useCardDrag = () => {
	const { reorderItems, canvas, setDragState } = useFlowStore(
		useShallow((state) => ({
			reorderItems: state.reorderItems,
			canvas: state.canvas,
			setDragState: state.setDragState
		}))
	);
	const nodeId = useNodeId();
	const { reorderCanvasItems } = useFlow();
	const [isDragging, setIsDragging] = useState(false);
	const dragY = useMotionValue(0);

	const { columnId, cardIndex } = useMemo(() => {
		for (const typeIdn in canvas.sections) {
			const items = canvas.sections[typeIdn];
			const idx = items.findIndex((item) => item.idn === nodeId);
			if (idx !== -1) {
				return { columnId: typeIdn, cardIndex: idx };
			}
		}
		return { columnId: '', cardIndex: -1 };
	}, [canvas.sections, nodeId]);

	// Get y-positions of all cards in the same column from react-flow store
	const columnCardPositions = useStore(
		useCallback(
			(s) => {
				const items = canvas.sections[columnId] ?? [];
				return items.map(
					(item) => s.nodeLookup.get(item.idn)?.position?.y ?? 0
				);
			},
			[canvas.sections, columnId]
		)
	);

	const getNodeEl = useCallback(
		() =>
			nodeId
				? document.querySelector<HTMLElement>(
						`.react-flow__node[data-id="${nodeId}"]`
					)
				: null,
		[nodeId]
	);

	// Find the closest card index to the dragged card's virtual position
	const getTargetIndex = useCallback(
		(accumulatedY: number): number => {
			if (columnCardPositions.length === 0 || cardIndex === -1) {
				return cardIndex;
			}
			const myY = columnCardPositions[cardIndex] ?? 0;
			const virtualY = myY + accumulatedY;

			let closest = cardIndex;
			let minDist = Number.POSITIVE_INFINITY;
			for (let i = 0; i < columnCardPositions.length; i++) {
				const dist = Math.abs(virtualY - columnCardPositions[i]);
				if (dist < minDist) {
					minDist = dist;
					closest = i;
				}
			}
			return closest;
		},
		[columnCardPositions, cardIndex]
	);

	const onDragEnd = useCallback(
		(offsetY: number) => {
			dragY.set(0);
			setIsDragging(false);
			setDragState(null);
			const currentElement = getNodeEl();
			currentElement?.classList.remove('dragging');
			if (currentElement) {
				currentElement.style.zIndex = '';
			}

			if (!nodeId || cardIndex === -1) {
				return;
			}

			const newIndex = getTargetIndex(offsetY);

			if (newIndex !== cardIndex) {
				reorderItems(columnId, cardIndex, newIndex);
				reorderCanvasItems();
			}
		},
		[
			cardIndex,
			columnId,
			dragY,
			getNodeEl,
			getTargetIndex,
			nodeId,
			reorderItems,
			reorderCanvasItems,
			setDragState
		]
	);

	const onDragHandlePointerDown = useCallback(
		(e: PointerEvent<HTMLButtonElement>) => {
			e.stopPropagation();

			const currentElement = getNodeEl();
			if (!currentElement) {
				return;
			}

			setIsDragging(true);
			currentElement?.classList.add('dragging');
			currentElement.style.zIndex = '1000';

			e.currentTarget.setPointerCapture(e.pointerId);

			let accumulatedY = 0;
			let lastY = e.clientY;
			let isFinalized = false;

			const finalizeDrag = () => {
				if (isFinalized) {
					return;
				}

				isFinalized = true;

				document.removeEventListener('pointermove', handlePointerMove);
				document.removeEventListener('pointerup', finalizeDrag);

				onDragEnd(accumulatedY);
			};

			const handlePointerMove = (moveEvent: globalThis.PointerEvent) => {
				const deltaY = moveEvent.clientY - lastY;
				lastY = moveEvent.clientY;
				accumulatedY += deltaY;
				dragY.set(accumulatedY);

				const columnItems = canvas.sections[columnId] ?? [];
				const toIndex = Math.max(
					0,
					Math.min(columnItems.length - 1, getTargetIndex(accumulatedY))
				);
				setDragState({ columnId, fromIndex: cardIndex, toIndex });
			};

			document.addEventListener('pointermove', handlePointerMove);
			document.addEventListener('pointerup', finalizeDrag);
		},
		[
			getNodeEl,
			dragY,
			onDragEnd,
			setDragState,
			cardIndex,
			columnId,
			canvas.sections,
			getTargetIndex
		]
	);

	return { isDragging, dragY, onDragEnd, onDragHandlePointerDown };
};
